import { DevTool } from '@hookform/devtools'
import { HelpOutline } from '@mui/icons-material'
import { Box, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { isEqual } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ChangeLeaderDialog, { ChangeLeaderDialogVariant } from '../../components/Dialog/ChangeLeaderDialog'
import ConfirmationDialog from '../../components/Dialog/ConfirmationDialog'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import EmploymentDescription from '../../components/Employment/EmploymentDescription'
import EmploymentManagement from '../../components/Employment/EmploymentManagement'
import Loading from '../../components/Loading/Loading'
import TechStackTable from '../../components/Table/TechStackTable'
import navigation from '../../config/navigation'
import { useAppDispatch, useEditMode, useNotification, useSwapLeaders } from '../../store/redux/hooks'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import {
  currentUserActions,
  getCurrentUser,
  getIsAdmin,
  getIsBaseUser,
  getIsManagement,
  getIsManagementOrAdministrator,
} from '../../store/redux/reducers/currentUserSlice'
import { NotificationActions } from '../../store/redux/reducers/notification'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import {
  useCreateUserMutation,
  useEditUserMutation,
  useLazyGetLeadershipDataQuery,
  useLazyGetUserByIdQuery,
} from '../../store/redux/reducers/usersSlice'
import { Project, User, UserFormValues, UsersTechnology } from '../../store/types'
import RoleType from '../../store/types/enums/RoleType'
import { defaultUserFormValues } from '../../store/types/form/UserFormValues'
import UserModifyDTO from '../../store/types/models/UserModifyDTO'
import { createErrorMessage, hasRole } from '../../store/utils'
import { changeNullsToEmptyString } from '../../store/utils/falsyValuesConverter'
import ProfileDescription from './ProfileDescription'
import ProfileViewActions from './ProfileViewActions'
import ProfileViewForm from './ProfileViewForm'
import ProfileViewHeader from './ProfileViewHeader'
import ProfileViewLeaderSection from './ProfileViewLeaderSection'
import ProfileViewRolesSection from './ProfileViewRolesSection'

type ProfileViewProps = {
  newUser: boolean
  myProfile?: boolean
}

const ProfileView = ({ newUser, myProfile }: ProfileViewProps) => {
  const { displayNotification } = useNotification()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  let { id: userId } = useParams()
  const { isEditMode, setIsEditMode } = useEditMode()

  const isAdmin = useSelector(getIsAdmin)
  const isManagement = useSelector(getIsManagement)
  const isManagementOrAdministrator = useSelector(getIsManagementOrAdministrator)
  const isBaseUser = useSelector(getIsBaseUser)
  const currentUser = useSelector(getCurrentUser)

  const displayEmploymentSection = (isManagementOrAdministrator || myProfile) && !newUser

  if (myProfile) {
    userId = currentUser?.id
  }

  useEffect(() => {
    dispatch(
      newUser
        ? setPageName(t('page.name.newEmployee'))
        : myProfile
        ? setPageName(t('page.name.myProfile'))
        : setPageName(t('page.name.userProfile'))
    )

    newUser
      ? dispatch(setPageIndex(PageIndex.USERS))
      : myProfile
      ? dispatch(setPageIndex(PageIndex.PROFILE))
      : dispatch(setPageIndex(PageIndex.USERS))
    dispatch(pageHeaderActions.setInitialState())
  }, [dispatch, myProfile, newUser, t])

  const [user, setUser] = useState<User | null>(null)
  useEffect(() => {
    setUser(myProfile && currentUser !== null ? currentUser : null)
  }, [currentUser, myProfile])

  useEffect(() => {
    if (!myProfile) {
      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(
        breadcrumbsItemsActions.setSelectedItems({
          users: user ? `${user.firstName} ${user.lastName}` : t('page.name.new'),
        })
      )
    }
  }, [dispatch, myProfile, user?.firstName, user?.lastName, t, user])

  const [getUserById] = useLazyGetUserByIdQuery()
  const [editUser] = useEditUserMutation()
  const { swapLeaders } = useSwapLeaders()

  const [defaultValues, setDefaultValues] = useState(defaultUserFormValues)

  const getDefaultValues = useCallback((userData: User | null) => {
    if (userData === null) {
      setDefaultValues(defaultUserFormValues)
      return
    }

    const updatedData: UserFormValues = {
      ...userData,
      birthDate: userData.birthDate ? dayjs(userData.birthDate).utc(true) : null,
      employmentPercentage: userData.employmentPercentage.toString(),
      vacationDays: userData.vacationDays ? userData.vacationDays.toString() : '0',
    }

    return updatedData
  }, [])

  const { handleSubmit, control, setValue, getValues, watch, reset, formState } = useForm<UserFormValues>({
    defaultValues: defaultValues,
  })

  const fetchUserData = useCallback(
    async (id: string | undefined) => {
      try {
        if (id) {
          const result = await getUserById(id)
          if (result.isSuccess) return result
          if (result.error)
            dispatch(
              NotificationActions.addNotification({
                message: t('page.usersProfile.alert.userNotFetched'),
                type: 'error',
              })
            )
        } else {
          NotificationActions.addNotification({
            message: t('page.usersProfile.alert.idNotProvided'),
            type: 'error',
          })
        }
      } catch (error) {
        NotificationActions.addNotification({
          message: `${t('page.usersProfile.alert.userNotFetched')}. ${error}`,
          type: 'error',
        })
      }
    },
    [dispatch, getUserById, t]
  )

  const refetch = useCallback(() => {
    fetchUserData(userId).then(response => {
      if (response?.data) {
        setUser(response.data)
      } else {
        setUser(null)
      }
    })
  }, [fetchUserData, userId])

  useEffect(() => {
    if (user) {
      const formValues = getDefaultValues(user)
      reset(formValues)
    } else {
      reset(defaultUserFormValues)
    }
  }, [getDefaultValues, user, userId, reset])

  useEffect(() => {
    refetch()
  }, [refetch, userId])

  useEffect(() => {
    setIsEditable(false)
  }, [userId])

  const [createUser] = useCreateUserMutation()

  const handleCreate = async (data: UserFormValues) => {
    try {
      await createUser(data).unwrap()
      displayNotification({ message: t('page.usersProfile.alert.userCreated'), type: 'success' })
      reset(defaultUserFormValues)
    } catch (error) {
      let message = `${t('page.usersProfile.alert.userCreatedError')}: ${createErrorMessage(error)}`
      displayNotification({ message: message, type: 'error' })
      reset(data)
    }
  }

  const [isEditable, setIsEditable] = useState(false)

  const defaultWithDate = () => {
    return {
      ...defaultValues,
      birthDate: defaultValues.birthDate,
      technologies: new Set(defaultValues.technologies),
      roles: new Set(defaultValues.roles),
      leaders: new Set(defaultValues.leaders),
    }
  }
  const currentWithDate = () => {
    return {
      ...getValues(),
      birthDate: getValues().birthDate,
      technologies: new Set(getValues().technologies),
      roles: new Set(getValues().roles),
      leaders: new Set(getValues().leaders),
    }
  }

  const handleEdit = async (data: UserFormValues, leadersEmployees?: string[], newLeaderId?: string) => {
    const updatedUser: Partial<UserModifyDTO> = {
      ...user,
      ...data,
      vacationDays: data.vacationDays,
    }

    if (isEqual(defaultWithDate(), currentWithDate())) {
      setIsEditable(false)
      setIsEditMode(false)
      return
    }

    try {
      const user = await editUser({ user: updatedUser, id: userId!!, leadersEmployees, newLeaderId }).unwrap()
      displayNotification({ message: t('page.usersProfile.saved'), type: 'success' })
      const userAfterEdit = changeNullsToEmptyString(user)
      setUser(userAfterEdit)
      if (myProfile) dispatch(currentUserActions.setCurrentUser({ currentUser: userAfterEdit as User }))
      setIsEditable(false)
      setIsEditMode(false)
      reset(data)
      if (data.employeeStatus === 'DELETED') {
        skipBlockerRef.current = true
        navigate(navigation.users, { replace: true })
      }
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    }
  }

  useEffect(() => {
    if (newUser) {
      setIsEditable(true)
      return
    }
    setIsEditable(false)
  }, [newUser])

  useEffect(() => {
    if (!isEditMode) setIsEditMode(newUser)
  }, [isEditMode, newUser, setIsEditMode])

  const handleChangeEditMode = (value: boolean) => {
    setIsEditable(value)
    setIsEditMode(value)
  }

  const handleSubmitCreate = () => {
    handleSubmit(data => handleCreate(data))()
  }

  const [openChangeLeaderForEmployeesOrProjectsDialog, setOpenChangeLeaderForEmployeesOrProjectsDialog] =
    useState<ChangeLeaderDialogVariant | null>(null)
  const [openDeleteStatusDialog, setOpenDeleteStatusDialog] = useState(false)
  const [openBlockStatusDialog, setOpenBlockStatusDialog] = useState(false)
  const [employeesOfLeader, setEmployeesOfLeader] = useState<User[]>([])
  const [getLeadershipData] = useLazyGetLeadershipDataQuery()
  const [leaderProjects, setLeaderProjects] = useState<Project[]>([])

  const fetchLeadershipData = async () => {
    const { data, isSuccess } = await getLeadershipData(user?.id ?? '')
    if (isSuccess && data) {
      setEmployeesOfLeader(data.employees)
      setLeaderProjects(data.projects)
      return { employeesLength: data.employees.length, projectsLength: data.projects.length }
    }
    return { employeesLength: 0, projectsLength: 0 }
  }

  const handleSubmitEdit = async () => {
    if (watch('employeeStatus') === 'DELETED') {
      const { employeesLength, projectsLength } = await fetchLeadershipData()
      if (employeesLength > 0 || projectsLength > 0) {
        setOpenChangeLeaderForEmployeesOrProjectsDialog('statusDeleted')
      } else {
        setOpenDeleteStatusDialog(true)
      }
      return
    }

    if (Object.keys(formState.errors).length === 0) {
      const isStatusChangingToBlocked = watch('employeeStatus') === 'BLOCKED' && user?.employeeStatus !== 'BLOCKED'
      if (isStatusChangingToBlocked) {
        const { employeesLength, projectsLength } = await fetchLeadershipData()
        if (employeesLength > 0 || projectsLength > 0) {
          setOpenChangeLeaderForEmployeesOrProjectsDialog('statusBlocked')
          return
        }
        setOpenBlockStatusDialog(true)
        return
      }

      const isLeaderRemoved =
        hasRole([RoleType.Leader], user?.roles ?? []) && !hasRole([RoleType.Leader], watch('roles'))
      const isProjectLeaderRemoved =
        hasRole([RoleType.ProjectLeader], user?.roles ?? []) && !hasRole([RoleType.ProjectLeader], watch('roles'))

      if (isLeaderRemoved || isProjectLeaderRemoved) {
        const { employeesLength, projectsLength } = await fetchLeadershipData()
        const relevantEmployeesLength = isLeaderRemoved ? employeesLength : 0
        const relevantProjectsLength = isProjectLeaderRemoved ? projectsLength : 0
        if (relevantEmployeesLength > 0 || relevantProjectsLength > 0) {
          setOpenChangeLeaderForEmployeesOrProjectsDialog('roleRemoved')
        } else {
          handleSubmit(data => handleEdit(data))()
        }
      } else {
        handleSubmit(data => handleEdit(data))()
      }
    } else handleSubmit(data => handleEdit(data))()
  }

  const handleSubmitEditWithLeaderChange = (substituteLeaderId: string, substituteProjectLeaderId: string) => {
    let leaderEmployeesIds = employeesOfLeader.map(e => e.id)
    handleSubmit(data => handleEdit(data, leaderEmployeesIds, substituteLeaderId))()
    swapLeaders(userId!, leaderProjects, substituteProjectLeaderId)
    setOpenChangeLeaderForEmployeesOrProjectsDialog(null)
  }

  const theme = useTheme()
  const isLeadersAndRolesVisible = (isEditable || newUser) && (isManagement || isAdmin)
  const isSidebarOpen = useSelector(getSidebarState)
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const formTableSectionBreakpoint = useMediaQuery(theme.breakpoints.down(1040))

  const customFormReset = useCallback(() => {
    if (user) {
      const updatedData: UserFormValues = {
        ...user,
        birthDate: user.birthDate ? dayjs(user.birthDate).utc(true) : null,
        leaders: user.leaders,
        roles: user.roles,
        employmentPercentage: user.employmentPercentage ? user.employmentPercentage.toString() : '0',
        vacationDays: user.vacationDays ? user.vacationDays.toString() : '0',
      }

      reset(updatedData)
    } else {
      reset(defaultUserFormValues)
    }

    setIsEditable(false)
    setIsEditMode(false)
  }, [user, reset, setIsEditMode])

  const isCurrentUserLeader = user?.leaders?.some(leader => leader.id === currentUser?.id)
  const areActionButtonsVisible = currentUser?.id === user?.id || isManagement || isCurrentUserLeader
  const isTechnologyEditable = newUser || (isEditable && (myProfile || isManagement || isCurrentUserLeader || isAdmin))

  const { replace } = useFieldArray({
    name: 'technologies',
    control,
  })

  const replaceArray = (technologies: UsersTechnology[]) => {
    return replace(technologies)
  }

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const skipBlockerRef = useRef(false)

  const checkUnsavedChanges = useCallback(() => {
    if (skipBlockerRef.current) return false
    return formState.isDirty
  }, [formState.isDirty])

  return user === null && !newUser ? (
    <Loading />
  ) : (
    <>
      <form noValidate>
        <Stack
          direction="column"
          gap={4}
          sx={{
            color: 'text.base',
            paddingBottom: 3,
          }}
        >
          <ProfileViewHeader
            user={user}
            setFormValue={setValue}
            isEditMode={isEditable}
            setIsEditMode={handleChangeEditMode}
            isNewUser={newUser}
            isMyProfile={myProfile || false}
            handleCreate={handleSubmitCreate}
            handleEdit={handleSubmitEdit}
            resetForm={customFormReset}
            isCurrentUserLeader={isCurrentUserLeader}
            formState={formState}
            setIsCancelDialogOpen={setIsCancelDialogOpen}
            checkUnsavedChanges={checkUnsavedChanges}
          />

          {areActionButtonsVisible && !isBaseUser && !newUser && !isEditable && isDownMd && user && (
            <ProfileViewActions
              user={user}
              isMyProfile={myProfile || false}
              isManagement={isManagement || false}
              isCurrentUserLeader={isCurrentUserLeader}
              displayTitle
            />
          )}

          <Stack
            direction={{
              xs: 'column',
              sm: 'column',
              md: isSidebarOpen || formTableSectionBreakpoint ? 'column' : 'row',
              lg: 'row',
            }}
            px={2}
            gap={'24px'}
          >
            <Stack
              direction={isDownSm || (isSm && isSidebarOpen) ? 'column' : 'row'}
              gap={2}
              sx={{ minWidth: 0 }}
              width={{
                xs: '100%',
                lg: isLeadersAndRolesVisible ? '50%' : '33%',
                xl: isLeadersAndRolesVisible ? '50%' : '50%',
              }}
            >
              <Stack direction="column" sx={{ flex: 1, minWidth: 0, height: 'auto' }} gap={2}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 2 }}>
                  <Typography component={'h3'} variant="h5" color="text.highEmphasis">
                    {t('page.usersProfile.form.headers.personal')}
                  </Typography>
                  {isEditable && (
                    <Typography variant="caption">{t('page.usersProfile.form.headers.requiredFields')}</Typography>
                  )}
                </Box>

                {isEditable || newUser ? (
                  <ProfileViewForm
                    isMyProfile={myProfile || false}
                    isNewUser={newUser}
                    isManagement={isManagement || false}
                    control={control}
                  />
                ) : (
                  <ProfileDescription user={user} />
                )}
              </Stack>

              {isLeadersAndRolesVisible && (
                <Stack direction="column" gap={2} sx={{ flex: 1, minWidth: 0 }}>
                  <ProfileViewRolesSection
                    control={control}
                    setValue={setValue}
                    user={user}
                    errors={formState.errors}
                    watch={watch}
                  />
                  <ProfileViewLeaderSection control={control} setValue={setValue} user={user} />
                </Stack>
              )}
            </Stack>

            <Stack
              direction="column"
              sx={{
                width: {
                  xs: '100%',
                  lg: isLeadersAndRolesVisible ? '50%' : '66%',
                  xl: isLeadersAndRolesVisible ? '50%' : '50%',
                },
                mb: 2,
                marginBottom: 0,
                minWidth: 0,
              }}
              gap={2}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <Typography component={'h3'} variant="h5" color="text.highEmphasis" id={'tech-header'}>
                  {t('page.usersProfile.form.headers.technologies')}
                </Typography>
                <Tooltip title={<Typography variant="caption">{t('page.usersProfile.tooltip.techStack')}</Typography>}>
                  <HelpOutline sx={{ color: 'text.mediumEmphasis', fontSize: 18 }} />
                </Tooltip>
              </Stack>

              <Controller
                control={control}
                name="technologies"
                render={() => (
                  <TechStackTable
                    editable={isTechnologyEditable}
                    replace={replaceArray}
                    watchedTechnologies={watch('technologies')}
                    userTechnologies={user?.technologies}
                  />
                )}
              />
            </Stack>
          </Stack>

          {displayEmploymentSection && (
            <>
              <Stack px={2} gap={'24px'} width={'100%'}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Typography component={'h3'} variant="h5" color="text.highEmphasis">
                    {t('page.usersProfile.form.headers.employment')}
                  </Typography>
                </Box>
                <EmploymentDescription isMyProfile={myProfile || false} />
              </Stack>

              <Stack px={2} gap={0} width={'100%'}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Typography component={'h3'} variant="h5" color="text.highEmphasis">
                    {t('page.usersProfile.form.headers.employmentHistory')}
                  </Typography>
                </Box>
                <EmploymentManagement
                  isEditable={isEditable}
                  isMyProfile={myProfile || false}
                  isManagementOrAdministrator={isManagementOrAdministrator || false}
                />
              </Stack>
            </>
          )}

          <DevTool control={control} />
        </Stack>
        <ConfirmationDialog
          open={openDeleteStatusDialog}
          setOpen={setOpenDeleteStatusDialog}
          title={t('page.usersProfile.deleteStatusConfirmation.title')}
          content={
            <Stack direction="column" gap={1}>
              <span>{t('page.usersProfile.deleteStatusConfirmation.content')}</span>
              <Typography variant="body2" color="error">
                {t('components.dialog.changeStatus.content.blockWarning')}
              </Typography>
            </Stack>
          }
          confirmationButtonText={t('page.usersProfile.deleteStatusConfirmation.confirm')}
          handleConfirm={() => {
            setOpenDeleteStatusDialog(false)
            handleSubmit(data => handleEdit(data))()
          }}
        />
        <ConfirmationDialog
          open={openBlockStatusDialog}
          setOpen={setOpenBlockStatusDialog}
          title={t('components.dialog.changeStatus.title')}
          content={
            <Stack direction="column" gap={1}>
              <span>
                <Trans
                  i18nKey="components.dialog.changeStatus.content.description"
                  values={{
                    name: `${user?.firstName} ${user?.lastName}`,
                    status: t('components.dialog.changeStatus.content.blocked'),
                  }}
                />
              </span>
              <Typography variant="body2" color="error">
                {t('components.dialog.changeStatus.content.blockWarning')}
              </Typography>
            </Stack>
          }
          handleConfirm={() => {
            setOpenBlockStatusDialog(false)
            handleSubmit(data => handleEdit(data))()
          }}
        />
        <ChangeLeaderDialog
          open={openChangeLeaderForEmployeesOrProjectsDialog !== null}
          onClose={() => setOpenChangeLeaderForEmployeesOrProjectsDialog(null)}
          handleSubmitEditWithLeaderChange={handleSubmitEditWithLeaderChange}
          employeesOfLeader={employeesOfLeader}
          projectsOfLeader={leaderProjects}
          isLoading={formState.isSubmitting || formState.isSubmitSuccessful}
          editedUser={user}
          variant={openChangeLeaderForEmployeesOrProjectsDialog ?? 'roleRemoved'}
        />
      </form>

      <DiscardChangesDialog
        checkUnsavedChanges={checkUnsavedChanges}
        resetForm={customFormReset}
        open={isCancelDialogOpen}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
    </>
  )
}

export default ProfileView
